import { CloseOutlined, TableOutlined, BulbOutlined, CheckOutlined, ExclamationCircleOutlined, PlayCircleOutlined, MailOutlined, HighlightOutlined, QuestionCircleOutlined, CodeOutlined, MoonOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { Switch, Select, Input, notification } from "antd";
import { useAppSelector, useAppDispatch, useDarkMode, useThemeColors } from "redux/hooks";
import { useSession } from "components/wrappers/SessionProvider";
import { UpdateSettingsAction } from "redux/actions/UpdateSettings";
import { ConditionalTooltip } from "./ConditionalTooltip";

export function SettingsView(props: {
    isOpen: boolean;
    onClose: () => void;
    mode: 'dashboard' | 'stream';
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const session = useSession();
    const theme = useAppSelector(state => state.user.settings?.theme ?? (state.user.settings?.dark_mode ? "dark" : "light"));
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();

    // Parse theme into color scheme and dark mode
    const parseTheme = (themeValue: string): { colorScheme: string; isDark: boolean } => {
        if (themeValue === "light" || themeValue === "dark") {
            return { colorScheme: "sentier", isDark: themeValue === "dark" };
        }
        // Split on the last hyphen to handle color schemes with hyphens (e.g., "high-contrast-dark")
        const lastHyphenIndex = themeValue.lastIndexOf("-");
        if (lastHyphenIndex !== -1) {
            const colorScheme = themeValue.substring(0, lastHyphenIndex);
            const mode = themeValue.substring(lastHyphenIndex + 1);
            if (mode === "dark" || mode === "light") {
                return { colorScheme, isDark: mode === "dark" };
            }
        }
        // Fallback
        return { colorScheme: "sentier", isDark: false };
    };

    const { colorScheme: currentColorScheme, isDark: currentIsDark } = parseTheme(theme);
    const pageSize = useAppSelector(state => state.user.settings?.page_size ?? "200");
    const showPageSize = useAppSelector(state => state.user.settings?.show_page_size ?? true);
    const suggestRequests = useAppSelector(state => state.user.settings?.suggest_requests ?? true);
    const animateResponses = useAppSelector(state => state.user.settings?.animate_responses ?? true);
    const showEmailToggle = useAppSelector(state => state.user.settings?.show_email_toggle ?? false);
    const allowEmail = useAppSelector(state => state.user.settings?.allow_email ?? false);
    const tooltipMode = useAppSelector(state => state.user.settings?.tooltip_mode ?? "small");
    const expandCodeblocks = useAppSelector(state => state.user.settings?.expand_codeblocks ?? true);
    const agentMemory = useAppSelector(state => state.user.settings?.agent_memory ?? "");
    const [localAgentMemory, setLocalAgentMemory] = useState(agentMemory);

    const isStreamMode = props.mode === 'stream';

    const buildThemeString = (colorScheme: string, isDark: boolean): string => {
        if (colorScheme === "sentier") {
            return isDark ? "dark" : "light";
        }
        return `${colorScheme}-${isDark ? "dark" : "light"}`;
    };

    const getThemeLabel = (colorScheme: string, isDark: boolean): string => {
        const colorLabels: Record<string, string> = {
            'sentier': 'Sentier',
            'astellas': 'Astellas',
            'ocean': 'Ocean',
            'forest': 'Forest',
            'high-contrast': 'High Contrast',
            'purple': 'Purple',
            'amber': 'Amber',
            'slate': 'Slate',
            'rose': 'Rose'
        };
        const modeLabel = isDark ? 'Dark' : 'Light';
        return `${colorLabels[colorScheme] || colorScheme} ${modeLabel}`;
    };

    const handleColorSchemeChange = async (colorScheme: string) => {
        const newTheme = buildThemeString(colorScheme, currentIsDark);
        try {
            await dispatch(UpdateSettingsAction(session, { theme: newTheme }));
            notification.success({
                message: 'Theme updated',
                description: `Theme has been set to ${getThemeLabel(colorScheme, currentIsDark)}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update theme',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleDarkModeChange = async (isDark: boolean) => {
        const newTheme = buildThemeString(currentColorScheme, isDark);
        try {
            await dispatch(UpdateSettingsAction(session, { theme: newTheme }));
            notification.success({
                message: 'Theme updated',
                description: `Theme has been set to ${getThemeLabel(currentColorScheme, isDark)}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update theme',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handlePageSizeChange = async (value: string) => {
        try {
            await dispatch(UpdateSettingsAction(session, { page_size: value }));
            notification.success({
                message: 'Page size updated',
                description: `Default page size has been set to ${value === '100000' ? 'All' : value}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update page size',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleSuggestRequestsChange = async (checked: boolean) => {
        try {
            await dispatch(UpdateSettingsAction(session, { suggest_requests: checked }));
            notification.success({
                message: 'Suggest requests updated',
                description: `Request suggestions have been ${checked ? 'enabled' : 'disabled'}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update suggest requests',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleAnimateResponsesChange = async (checked: boolean) => {
        try {
            await dispatch(UpdateSettingsAction(session, { animate_responses: checked }));
            notification.success({
                message: 'Animate responses updated',
                description: `Response animations have been ${checked ? 'enabled' : 'disabled'}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update animate responses',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleShowEmailToggleChange = async (checked: boolean) => {
        try {
            await dispatch(UpdateSettingsAction(session, { show_email_toggle: checked }));
            notification.success({
                message: 'Email toggle updated',
                description: `Email toggle button has been ${checked ? 'enabled' : 'disabled'}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update email toggle',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleTooltipModeChange = async (value: "large" | "small" | "none") => {
        try {
            await dispatch(UpdateSettingsAction(session, { tooltip_mode: value }));
            notification.success({
                message: 'Tooltip mode updated',
                description: `Tooltip mode set to ${value === "large" ? "Large" : value === "small" ? "Small" : "None"}.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update tooltip mode',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    const handleExpandCodeblocksChange = async (checked: boolean) => {
        try {
            await dispatch(UpdateSettingsAction(session, { expand_codeblocks: checked }));
            notification.success({
                message: 'Expand codeblocks updated',
                description: `Code blocks will ${checked ? 'expand' : 'collapse'} by default.`,
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to update expand codeblocks',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        }
    };

    // Sync local state with Redux state when it changes externally
    useEffect(() => {
        setLocalAgentMemory(agentMemory);
    }, [agentMemory]);

    const handleAgentMemoryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalAgentMemory(e.target.value);
    };

    const hasUnsavedChanges = localAgentMemory !== agentMemory;

    const handleAgentMemorySave = async () => {
        if (!hasUnsavedChanges) return;

        try {
            await dispatch(UpdateSettingsAction(session, { agent_memory: localAgentMemory }));
            notification.success({
                message: 'Agent memory saved',
                description: 'Your agent memory has been updated.',
                placement: 'bottomLeft',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Failed to save agent memory',
                description: 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
            // Revert to Redux value on error
            setLocalAgentMemory(agentMemory);
        }
    };

    // Ensure the Ant Select components do not show native title tooltips
    useEffect(() => {
        if (!props.isOpen) return;

        const removeSelectTitles = () => {
            const selectors = [
                '.settings-theme-select .ant-select-selection-item',
                        '.settings-page-size-select .ant-select-selection-item',
                        '.settings-tooltip-select .ant-select-selection-item'
            ];

            selectors.forEach(selector => {
                cardRef.current?.querySelectorAll(selector).forEach(node => {
                    node.removeAttribute('title');
                });
            });
        };

        removeSelectTitles();

        const observerTargets = [
            cardRef.current?.querySelector('.settings-theme-select'),
            cardRef.current?.querySelector('.settings-page-size-select'),
            cardRef.current?.querySelector('.settings-tooltip-select')
        ].filter((node): node is Element => Boolean(node));

        const observer = new MutationObserver(removeSelectTitles);
        observerTargets.forEach(target => {
            observer.observe(target, { childList: true, subtree: true, attributes: true });
        });

        return () => observer.disconnect();
    }, [props.isOpen]);

    // Handle clicking outside the card
    useEffect(() => {
        if (!props.isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is on the dropdown (which is rendered in a portal)
            const dropdown = document.querySelector('.settings-page-size-dropdown');
            if (dropdown && dropdown.contains(target)) {
                return; // Don't close if clicking on dropdown
            }

            // Also check if click is on any ant-select dropdown
            if (target.closest('.ant-select-dropdown')) {
                return; // Don't close if clicking on any select dropdown
            }

            if (cardRef.current && !cardRef.current.contains(target)) {
                props.onClose();
            }
        };

        // Add event listener with a small delay to prevent immediate closing
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [props.isOpen, props.onClose]);

    if (!props.isOpen) {
        return null;
    }

    return (
        <>
            <style>{`
                .ant-switch-checked {
                    background-color: ${themeColors.primaryColor} !important;
                }
                .ant-switch-checked:hover:not(.ant-switch-disabled) {
                    background-color: ${themeColors.primaryColorHover} !important;
                }
                /* The Settings overlay sits at z-index 2000, but antd Select
                   dropdowns are portaled to document.body with z-index 1050 by
                   default — so they render BEHIND the overlay and become
                   unclickable. Lift these three popups above the overlay. */
                .settings-page-size-dropdown,
                .settings-theme-dropdown,
                .settings-tooltip-dropdown {
                    z-index: 2001 !important;
                }
                .settings-page-size-dropdown .ant-select-item,
                .settings-theme-dropdown .ant-select-item,
                .settings-tooltip-dropdown .ant-select-item {
                    padding: 1px 6px !important;
                    min-height: auto !important;
                    height: auto !important;
                }
                .settings-page-size-dropdown .ant-select-item-option-content,
                .settings-theme-dropdown .ant-select-item-option-content,
                .settings-tooltip-dropdown .ant-select-item-option-content {
                    line-height: 20px !important;
                }
                ${darkMode ? `
                    .settings-page-size-dropdown,
                    .settings-theme-dropdown,
                    .settings-tooltip-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                    }
                    .settings-page-size-dropdown .ant-select-dropdown,
                    .settings-theme-dropdown .ant-select-dropdown,
                    .settings-tooltip-dropdown .ant-select-dropdown {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border: 1px solid ${themeColors.borderColor} !important;
                        border-color: ${themeColors.borderColor} !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                        outline: none !important;
                    }
                    .settings-page-size-dropdown .ant-select-dropdown,
                    .settings-page-size-dropdown .ant-select-dropdown > *,
                    .settings-theme-dropdown .ant-select-dropdown,
                    .settings-theme-dropdown .ant-select-dropdown > *,
                    .settings-tooltip-dropdown .ant-select-dropdown,
                    .settings-tooltip-dropdown .ant-select-dropdown > * {
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .settings-page-size-dropdown .ant-select-item,
                    .settings-theme-dropdown .ant-select-item,
                    .settings-tooltip-dropdown .ant-select-item {
                        color: ${themeColors.textColor} !important;
                    }
                    .settings-page-size-dropdown .ant-select-item:hover,
                    .settings-theme-dropdown .ant-select-item:hover,
                    .settings-tooltip-dropdown .ant-select-item:hover {
                        background-color: ${themeColors.hoverBackground} !important;
                    }
                    .settings-page-size-dropdown .ant-select-item-option-selected,
                    .settings-theme-dropdown .ant-select-item-option-selected,
                    .settings-tooltip-dropdown .ant-select-item-option-selected {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .settings-page-size-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled),
                    .settings-theme-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled),
                    .settings-tooltip-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
                        background-color: ${themeColors.selectedBackground} !important;
                    }
                    .settings-page-size-select .ant-select-selector,
                    .settings-theme-select .ant-select-selector,
                    .settings-tooltip-select .ant-select-selector {
                        background-color: ${themeColors.backgroundColorSecondary} !important;
                        border-color: ${themeColors.borderColor} !important;
                    }
                    .settings-page-size-select .ant-select-selection-item,
                    .settings-theme-select .ant-select-selection-item,
                    .settings-tooltip-select .ant-select-selection-item {
                        color: ${themeColors.textColor} !important;
                    }
                .settings-page-size-select .ant-select-arrow,
                .settings-theme-select .ant-select-arrow,
                .settings-tooltip-select .ant-select-arrow {
                    color: ${themeColors.textColor} !important;
                }
                ` : ''}
            `}</style>
            {/* Overlay */}
            <div
                onClick={props.onClose}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* Settings Card */}
                <div
                    ref={cardRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: themeColors.backgroundColor,
                        borderRadius: "8px",
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "600px",
                        maxWidth: "90vw",
                        maxHeight: "80vh",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header with close button */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            borderBottom: `1px solid ${themeColors.borderColor}`,
                        }}
                    >
                        <h2 style={{ margin: 0, fontSize: "1.5em", fontWeight: "600", color: themeColors.textColor }}>
                            Settings
                        </h2>
                        <ConditionalTooltip title="Close settings">
                            <button
                                onClick={props.onClose}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: themeColors.iconColor,
                                    fontSize: "18px",
                                }}
                            >
                                <CloseOutlined />
                            </button>
                        </ConditionalTooltip>
                    </div>

                    {/* Content area */}
                    <div
                        style={{
                            padding: "20px",
                            flex: 1,
                            overflow: "auto",
                        }}
                    >
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "16px 0"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <MoonOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                    Dark mode
                                </span>
                            </div>
                            <ConditionalTooltip title="Toggle dark mode">
                            <Switch checked={currentIsDark} onChange={handleDarkModeChange} />
                            </ConditionalTooltip>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "16px 0"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <HighlightOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                    Color scheme
                                </span>
                            </div>
                            <ConditionalTooltip title="Choose the application color scheme">
                            <Select
                                value={currentColorScheme}
                                onChange={handleColorSchemeChange}
                                size="small"
                                style={{ width: 140 }}
                                popupClassName="settings-theme-dropdown"
                                className="settings-theme-select"
                                options={[
                                    { value: 'sentier', label: 'Sentier' },
                                    { value: 'astellas', label: 'Astellas' },
                                    { value: 'ocean', label: 'Ocean' },
                                    { value: 'forest', label: 'Forest' },
                                    { value: 'high-contrast', label: 'High Contrast' },
                                    { value: 'purple', label: 'Purple' },
                                    { value: 'amber', label: 'Amber' },
                                    { value: 'slate', label: 'Slate' },
                                    { value: 'rose', label: 'Rose' }
                                ]}
                                popupMatchSelectWidth={false}
                            />
                            </ConditionalTooltip>
                        </div>
                        {isStreamMode && showPageSize && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <TableOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Default page size
                                    </span>
                                </div>
                                <ConditionalTooltip title="Default number of rows returned per page">
                                <Select
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    size="small"
                                    style={{ width: 120 }}
                                    popupClassName="settings-page-size-dropdown"
                                    className="settings-page-size-select"
                                    options={[
                                        { value: '50', label: '50' },
                                        { value: '100', label: '100' },
                                        { value: '200', label: '200' },
                                        { value: '500', label: '500' },
                                        { value: '1000', label: '1000' },
                                        { value: '100000', label: 'All' }
                                    ]}
                                    popupMatchSelectWidth={false}
                                />
                                </ConditionalTooltip>
                            </div>
                        )}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "16px 0"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <QuestionCircleOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                    Tooltips
                                </span>
                            </div>
                            <ConditionalTooltip title="Tooltip style for buttons/controls">
                            <Select
                                value={tooltipMode}
                                onChange={handleTooltipModeChange}
                                size="small"
                                style={{ width: 120 }}
                                popupClassName="settings-tooltip-dropdown"
                                className="settings-tooltip-select"
                                options={[
                                    { value: "large", label: "Large" },
                                    { value: "small", label: "Small" },
                                    { value: "none", label: "None" }
                                ]}
                                popupMatchSelectWidth={false}
                            />
                            </ConditionalTooltip>
                        </div>
                        {isStreamMode && (
                            <div style={{
                                borderTop: `1px solid ${themeColors.borderColor}`,
                                margin: "8px 0"
                            }} />
                        )}
                        {isStreamMode && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <CodeOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Expand codeblocks
                                    </span>
                                </div>
                                <ConditionalTooltip title="Code blocks will be expanded by default when enabled">
                                <Switch checked={expandCodeblocks} onChange={handleExpandCodeblocksChange} />
                                </ConditionalTooltip>
                            </div>
                        )}
                        {isStreamMode && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <BulbOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Suggest requests
                                    </span>
                                </div>
                                <ConditionalTooltip title="Show suggested follow-up requests while composing">
                                <Switch checked={suggestRequests} onChange={handleSuggestRequestsChange} />
                                </ConditionalTooltip>
                            </div>
                        )}
                        {isStreamMode && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <PlayCircleOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Animate responses
                                    </span>
                                </div>
                                <ConditionalTooltip title="Animate agent responses as they stream in">
                                <Switch checked={animateResponses} onChange={handleAnimateResponsesChange} />
                                </ConditionalTooltip>
                            </div>
                        )}
                        {isStreamMode && allowEmail && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 0"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <MailOutlined style={{ fontSize: "18px", color: themeColors.iconColor }} />
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Show email toggle
                                    </span>
                                </div>
                                <ConditionalTooltip title="Display the email toggle beside the response composer">
                                <Switch checked={showEmailToggle} onChange={handleShowEmailToggleChange} />
                                </ConditionalTooltip>
                            </div>
                        )}
                        {isStreamMode && (
                            <div style={{
                                padding: "16px 0"
                            }}>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "8px"
                                }}>
                                    <span style={{ fontSize: "15px", fontWeight: "500", color: themeColors.textColor }}>
                                        Agent memory
                                    </span>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px"
                                    }}>
                                        {hasUnsavedChanges && (
                                            <span style={{
                                                fontSize: "12px",
                                                color: darkMode ? "#ffa940" : "#fa8c16",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}>
                                                <ExclamationCircleOutlined />
                                                Unsaved
                                            </span>
                                        )}
                                        <ConditionalTooltip title="Save agent memory">
                                            <button
                                                onClick={handleAgentMemorySave}
                                                disabled={!hasUnsavedChanges}
                                                style={{
                                                    background: hasUnsavedChanges ? themeColors.primaryColor : themeColors.borderColorSecondary,
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    padding: "4px 12px",
                                                    cursor: hasUnsavedChanges ? "pointer" : "not-allowed",
                                                    color: "white",
                                                    fontSize: "13px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    transition: "background-color 0.2s"
                                                }}
                                            >
                                                <CheckOutlined />
                                                Save
                                            </button>
                                        </ConditionalTooltip>
                                    </div>
                                </div>
                                <ConditionalTooltip title="Persistent instructions sent with every request to the agent">
                                <Input.TextArea
                                    value={localAgentMemory}
                                    onChange={handleAgentMemoryChange}
                                    placeholder="Enter agent memory..."
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                    style={{
                                        width: "100%",
                                        backgroundColor: themeColors.backgroundColor,
                                        color: themeColors.textColor,
                                        borderColor: themeColors.borderColorSecondary
                                    }}
                                />
                                </ConditionalTooltip>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

